import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { D365TextField } from '@/components/D365FormControls';

import { searchClients } from '@/features/clients/api/clients';
import {
  getGroups,
  saveGroup,
  syncGroupClients,
  type ClientGroup,
} from '@/features/communications/api/communications';

export function GroupFormPage() {
  const navigate = useNavigate();
  const { groupId } = useParams();
  const isNew = groupId === 'new';
  const id = isNew ? null : Number(groupId);
  const groups = useQuery({
    queryKey: ['groups'],
    queryFn: getGroups,
    enabled: !isNew && Number.isInteger(id) && Number(id) > 0,
  });
  if ((!isNew && (!Number.isInteger(id) || Number(id) <= 0)) || groups.isError)
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        This group could not be loaded.
      </Alert>
    );
  if (!isNew && groups.isLoading)
    return (
      <Box sx={{ p: 4, display: 'grid', placeItems: 'center' }}>
        <CircularProgress aria-label="Loading group" />
      </Box>
    );
  const group = isNew ? null : groups.data?.find((candidate) => candidate.id === id);
  if (!isNew && !group)
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        This group could not be loaded.
      </Alert>
    );
  return (
    <GroupEditor
      key={id ?? 'new'}
      group={group ?? null}
      onClose={() => void navigate('/communications')}
    />
  );
}

function GroupEditor({ group, onClose }: { group: ClientGroup | null; onClose: () => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState(group?.name ?? '');
  const [selected, setSelected] = useState(group?.clients.map((client) => client.id) ?? []);
  const [savedState, setSavedState] = useState({
    name: group?.name ?? '',
    selected: group?.clients.map((client) => client.id) ?? [],
  });
  const clients = useQuery({
    queryKey: ['clients', 'group-options'],
    queryFn: () => searchClients(),
  });
  const save = useMutation({
    mutationFn: async () => {
      const saved = await saveGroup(name.trim(), group?.id);
      await syncGroupClients(saved.id, selected);
      return saved;
    },
    onSuccess: async (saved) => {
      await qc.invalidateQueries({ queryKey: ['groups'] });
      setSavedState({ name, selected: [...selected] });
      if (!group) void navigate(`/communications/groups/${saved.id}`, { replace: true });
    },
  });
  return (
    <Box sx={{ p: 1 }}>
      <Paper elevation={0} sx={{ ...paperSx, mb: 1.5, px: 1, py: 0.5 }}>
        <Stack direction="row" minHeight={44}>
          <Button sx={commandButtonSx} startIcon={<ArrowBackIcon />} onClick={onClose}>
            Back
          </Button>
          <Button
            sx={commandButtonSx}
            startIcon={<SaveOutlinedIcon />}
            disabled={
              !name.trim() ||
              save.isPending ||
              (name === savedState.name &&
                JSON.stringify([...selected].sort()) === JSON.stringify([...savedState.selected].sort()))
            }
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </Stack>
      </Paper>
      <Paper elevation={0} sx={{ ...paperSx, p: 2.5 }}>
        <Typography variant="h1">{group ? group.name : 'New Group'}</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Choose the clients included in this communications audience.
        </Typography>
        <Stack spacing={2} maxWidth={680}>
          <D365TextField
            label="Group name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
          />
          <Typography fontWeight={600}>Clients</Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              maxHeight: 420,
              overflowY: 'auto',
            }}
          >
            {(clients.data ?? []).map((client) => (
              <FormControlLabel
                key={client.id}
                control={
                  <Checkbox
                    checked={selected.includes(client.id)}
                    onChange={() =>
                      setSelected((old) =>
                        old.includes(client.id)
                          ? old.filter((id) => id !== client.id)
                          : [...old, client.id],
                      )
                    }
                  />
                }
                label={client.name}
              />
            ))}
          </Box>
          {save.isError && <Alert severity="error">The group could not be saved.</Alert>}
        </Stack>
      </Paper>
    </Box>
  );
}
const paperSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  boxShadow: '0 2px 6px rgba(0,0,0,.12)',
};
const commandButtonSx = {
  color: 'text.primary',
  '& .MuiButton-startIcon': { color: 'primary.main' },
};


